import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronsUpDown, Mail, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { api } from "../../lib/api.js";
import { useToast } from "../../components/Toast.js";
import { CreateTeamDialog } from "./CreateTeamDialog.js";
import { useSwitchTeam } from "./useSwitchTeam.js";

/**
 * Native team switcher — sits top-left, next to the logo, as the app's
 * primary "which workspace am I in" control (replaces Clerk's
 * <OrganizationSwitcher>). Team list + active team come from /api/me
 * (server truth); selecting a team re-scopes the whole app via
 * useSwitchTeam. The trailing action creates a new team.
 *
 * While impersonating it renders read-only: the admin is viewing the
 * TARGET's session and shouldn't mutate their active team.
 */
export function TeamSwitcher({
	impersonating = false,
	teamName,
}: {
	impersonating?: boolean;
	teamName?: string | null;
}) {
	if (impersonating) return <ImpersonatedTeamChip name={teamName} />;
	return <SwitcherMenu />;
}

function SwitcherMenu() {
	const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), retry: false });
	const switchTeam = useSwitchTeam();
	const { push } = useToast();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [open, setOpen] = useState(false);
	const [creating, setCreating] = useState(false);
	const [switching, setSwitching] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	// Invitations + teams open to the caller's email domain — surfaced here
	// (not only on the no-team gate) because a user who already has their own
	// workspace would otherwise have NO way to discover their company's team.
	// Fetched lazily when the menu opens; the /api/me badge count keeps the
	// trigger honest without the extra request.
	const my = useQuery({
		queryKey: ["my-invites"],
		queryFn: () => api.myInvites(),
		enabled: open,
	});
	const acceptInvite = useMutation({
		mutationFn: (id: string) => api.acceptMyInvite(id),
		onSuccess: async (r) => {
			setOpen(false);
			await qc.invalidateQueries({ queryKey: ["my-invites"] });
			await switchTeam(r.orgId);
			await navigate({ to: "/docs" });
		},
		onError: (err) => push((err as Error).message, "error"),
	});
	const joinSuggestion = useMutation({
		mutationFn: (orgId: string) => api.joinSuggestedTeam(orgId),
		onSuccess: async (r) => {
			setOpen(false);
			await qc.invalidateQueries({ queryKey: ["my-invites"] });
			if (r.joined && r.orgId) {
				await switchTeam(r.orgId);
				await navigate({ to: "/docs" });
			} else {
				push("Request sent — a team admin will approve it.", "success");
			}
		},
		onError: (err) => push((err as Error).message, "error"),
	});

	// Dismiss on outside click / Escape — standard menu behaviour.
	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const teams = me.data?.teams ?? [];
	const active = me.data?.activeOrg ?? null;

	const select = async (orgId: string) => {
		setOpen(false);
		if (orgId === active?.id || switching) return;
		setSwitching(true);
		try {
			await switchTeam(orgId);
			await navigate({ to: "/docs" });
		} catch {
			push("Couldn't switch teams. Please try again.", "error");
		} finally {
			setSwitching(false);
		}
	};

	return (
		<div
			ref={rootRef}
			className="relative flex items-center gap-2 min-w-0"
			data-testid="org-switcher"
		>
			{/* The "Team" eyebrow is a label for a control that already reads as
			    one. It only earns its width where there is width to spare. */}
			<span className="hidden lg:flex items-center gap-1.5 mx-eyebrow text-white/40 shrink-0">
				<Building2 className="h-3.5 w-3.5" aria-hidden="true" />
				Team
			</span>
			<button
				type="button"
				data-testid="team-switcher-trigger"
				aria-haspopup="menu"
				aria-expanded={open}
				disabled={switching}
				onClick={() => setOpen((o) => !o)}
				// A long team name must cost the header nothing: the button
				// truncates, and its ceiling grows with the room available.
				className="inline-flex items-center gap-2 rounded-md border mx-divider bg-white/[0.04] hover:bg-white/[0.08] px-2.5 py-1.5 text-[13px] text-white/85 min-w-0 max-w-[8.5rem] sm:max-w-[11rem] lg:max-w-[13rem] transition-colors disabled:opacity-60"
			>
				<span className="truncate">
					{switching ? "Switching…" : (active?.name || "Select a team")}
				</span>
				{(me.data?.pendingInviteCount ?? 0) > 0 && (
					<span
						data-testid="invite-badge"
						title="You have pending team invitations"
						className="h-1.5 w-1.5 rounded-full bg-[var(--color-mx-electric)] shrink-0"
					/>
				)}
				<ChevronsUpDown
					className="h-3.5 w-3.5 shrink-0 text-white/40"
					aria-hidden="true"
				/>
			</button>

			{open && (
				<div
					role="menu"
					data-testid="team-switcher-menu"
					className="absolute left-0 top-full mt-2 z-50 min-w-[15rem] rounded-md border mx-divider bg-[#141517] shadow-xl shadow-black/50 py-1.5"
				>
					<p className="mx-eyebrow text-white/35 px-3 pt-1 pb-2">Your teams</p>
					{teams.map((t) => (
						<button
							key={t.id}
							type="button"
							role="menuitem"
							data-testid={`team-option-${t.id}`}
							onClick={() => void select(t.id)}
							className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-white/85 hover:bg-white/[0.06]"
						>
							<span className="flex-1 truncate">{t.name}</span>
							{t.kind === "preload" ? (
								<span
									className="mx-eyebrow text-[9px] text-[var(--color-mx-electric)]/80"
									title="A preloaded brand you're working on in admin mode"
								>
									admin mode
								</span>
							) : t.role === "admin" && (
								<span className="mx-eyebrow text-[9px] text-white/35">
									admin
								</span>
							)}
							{t.id === active?.id ? (
								<Check
									className="h-3.5 w-3.5 text-[var(--color-mx-electric)]"
									aria-hidden="true"
								/>
							) : (
								<span className="w-3.5" aria-hidden="true" />
							)}
						</button>
					))}
					{teams.length === 0 && (
						<p className="px-3 py-2 text-[13px] text-white/45">
							You're not in a team yet.
						</p>
					)}

					{(my.data?.invites.length ?? 0) > 0 && (
						<div
							className="mt-1 pt-1 border-t mx-divider"
							data-testid="switcher-invites"
						>
							<p className="mx-eyebrow text-white/35 px-3 pt-1 pb-2">
								Invitations
							</p>
							{my.data!.invites.map((inv) => (
								<button
									key={inv.id}
									type="button"
									role="menuitem"
									data-testid={`switcher-invite-${inv.id}`}
									disabled={acceptInvite.isPending}
									onClick={() => acceptInvite.mutate(inv.id)}
									className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-white/85 hover:bg-white/[0.06] disabled:opacity-60"
								>
									<Mail
										className="h-3.5 w-3.5 text-[var(--color-mx-electric)]"
										aria-hidden="true"
									/>
									<span className="flex-1 truncate">{inv.orgName}</span>
									<span className="text-[11px] text-[var(--color-mx-electric)]">
										Join
									</span>
								</button>
							))}
						</div>
					)}

					{(my.data?.suggestions.length ?? 0) > 0 && (
						<div
							className="mt-1 pt-1 border-t mx-divider"
							data-testid="switcher-suggestions"
						>
							<p className="mx-eyebrow text-white/35 px-3 pt-1 pb-2">
								At your company
							</p>
							{my.data!.suggestions.map((s) => (
								<button
									key={s.orgId}
									type="button"
									role="menuitem"
									data-testid={`switcher-suggestion-${s.orgId}`}
									disabled={joinSuggestion.isPending}
									onClick={() => joinSuggestion.mutate(s.orgId)}
									className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-white/85 hover:bg-white/[0.06] disabled:opacity-60"
								>
									<Building2
										className="h-3.5 w-3.5 text-white/40"
										aria-hidden="true"
									/>
									<span className="flex-1 truncate">{s.orgName}</span>
									<span className="text-[11px] text-white/45">
										{s.mode === "auto_join" ? "Join" : "Request"}
									</span>
								</button>
							))}
						</div>
					)}

					<div className="mt-1 pt-1 border-t mx-divider">
						<button
							type="button"
							role="menuitem"
							data-testid="team-create-open"
							onClick={() => {
								setOpen(false);
								setCreating(true);
							}}
							className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-white/65 hover:text-white hover:bg-white/[0.06]"
						>
							<Plus className="h-3.5 w-3.5" aria-hidden="true" />
							Create team
						</button>
					</div>
				</div>
			)}

			{creating && <CreateTeamDialog onClose={() => setCreating(false)} />}
		</div>
	);
}

/** Read-only team indicator shown in place of the switcher while
 *  impersonating — names the *impersonated* user's active team (from the
 *  server session). Non-interactive by design: an admin shouldn't switch
 *  the target's team out from under them. */
function ImpersonatedTeamChip({ name }: { name?: string | null }) {
	return (
		<span
			data-testid="impersonated-team-chip"
			title="Active team of the user you're viewing as"
			className="inline-flex items-center gap-2 rounded-md border border-[var(--color-mx-gold)]/40 bg-[var(--color-mx-gold)]/10 px-2.5 py-1.5 text-[13px] text-[var(--color-mx-gold)] max-w-[12rem]"
		>
			<Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
			<span className="truncate">{name || "Their team"}</span>
		</span>
	);
}
